import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "../context/AppContext";
import api from "../lib/api";
import { toast } from "sonner";
import { MessageCircle, X, Copy, Check } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";

const WHATSAPP = process.env.REACT_APP_WHATSAPP_NUMBER;

export default function Checkout() {
  const { t, lang, cart, cartTotal, clearCart } = useApp();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", phone: "", address: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [business, setBusiness] = useState(null);
  const [payModal, setPayModal] = useState(null);
  const [copied, setCopied] = useState(false);
  const [couponCode, setCouponCode] = useState("");
  const [couponInfo, setCouponInfo] = useState(null); // {discount_amount, final_total}
  const [couponError, setCouponError] = useState("");

  useEffect(() => {
    api.get("/business").then((r) => setBusiness(r.data)).catch(() => {});
  }, []);

  // Redirect empty cart to products page — but only if no order was just placed (no modal showing).
  useEffect(() => {
    if (cart.length === 0 && !payModal) {
      navigate("/products", { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const buildWhatsAppMessage = (orderId, trackingCode) => {
    const trackUrl = `${window.location.origin}/track/${trackingCode}`;
    const lines = [
      `*New Order — SVRM*`,
      `Order #: ${trackingCode}`,
      `Name: ${form.name}`,
      `Phone: ${form.phone}`,
      `Address: ${form.address}`,
      form.notes ? `Notes: ${form.notes}` : null,
      ``,
      `*Items:*`,
      ...cart.map(
        (it) =>
          `• ${it.name_en} — ${it.weight_kg}kg × ${it.quantity} = ₹${(
            it.weight_kg * it.price_per_kg * it.quantity
          ).toFixed(0)}`
      ),
      ``,
      `*Total: ₹${cartTotal.toFixed(0)}*`,
      ``,
      `Track your order: ${trackUrl}`,
    ].filter(Boolean);
    return lines.join("\n");
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.address) {
      toast.error(lang === "en" ? "Please fill all required fields" : "అన్ని వివరాలను నింపండి");
      return;
    }
    setSubmitting(true);
    try {
      const items = cart.map((it) => ({
        product_id: it.product_id,
        name_en: it.name_en,
        name_te: it.name_te,
        weight_kg: it.weight_kg,
        price_per_kg: it.price_per_kg,
        quantity: it.quantity,
        subtotal: it.weight_kg * it.price_per_kg * it.quantity,
      }));
      const total = cartTotal;
      const orderPayload = {
        customer_name: form.name,
        customer_phone: form.phone,
        customer_address: form.address,
        notes: form.notes,
        items,
        payment_method: "whatsapp",
      };
      if (couponInfo) orderPayload.coupon_code = couponCode.trim().toUpperCase();
      const { data } = await api.post("/orders", orderPayload);
      toast.success(t.checkout.successMsg);
      const whatsappMsg = buildWhatsAppMessage(data.id, data.tracking_code);
      setPayModal({
        orderId: data.id,
        trackingCode: data.tracking_code,
        total,
        whatsappMsg,
      });
      clearCart();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to place order");
    } finally {
      setSubmitting(false);
    }
  };

  const closePayModal = () => {
    setPayModal(null);
    navigate("/", { replace: true });
  };

  const sendOnWhatsApp = () => {
    if (!payModal) return;
    const msg = encodeURIComponent(payModal.whatsappMsg);
    window.open(`https://wa.me/${WHATSAPP}?text=${msg}`, "_blank");
  };

  const copyUpiId = async () => {
    if (!business?.upi_id) return;
    try {
      await navigator.clipboard.writeText(business.upi_id);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy");
    }
  };

  const upiLink =
    business?.upi_id && payModal
      ? `upi://pay?pa=${encodeURIComponent(business.upi_id)}&pn=${encodeURIComponent(
          business.upi_name || "SVRM"
        )}&am=${payModal.total.toFixed(2)}&cu=INR&tn=${encodeURIComponent(
          `SVRM Order ${payModal.orderId.slice(0, 8).toUpperCase()}`
        )}`
      : "";

  return (
    <div data-testid="checkout-page" className="bg-[#fbf8f1] min-h-[80vh]">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14 grid grid-cols-1 lg:grid-cols-5 gap-10">
        <form onSubmit={onSubmit} className="lg:col-span-3 space-y-5">
          <h1 className={`text-4xl tracking-tighter font-bold text-stone-900 ${lang === "te" ? "font-telugu" : ""}`}>
            {t.checkout.title}
          </h1>
          <div>
            <label className="text-xs tracking-wider uppercase text-stone-500 font-semibold">{t.checkout.name}</label>
            <input
              data-testid="checkout-name"
              name="name"
              value={form.name}
              onChange={onChange}
              className="mt-1 w-full bg-[#fafaf9] border border-stone-300 rounded-sm px-3 py-2.5 focus:outline-none focus:border-[#274e13]"
              required
            />
          </div>
          <div>
            <label className="text-xs tracking-wider uppercase text-stone-500 font-semibold">{t.checkout.phone}</label>
            <input
              data-testid="checkout-phone"
              name="phone"
              value={form.phone}
              onChange={onChange}
              className="mt-1 w-full bg-[#fafaf9] border border-stone-300 rounded-sm px-3 py-2.5 focus:outline-none focus:border-[#274e13]"
              required
            />
          </div>
          <div>
            <label className="text-xs tracking-wider uppercase text-stone-500 font-semibold">{t.checkout.address}</label>
            <textarea
              data-testid="checkout-address"
              name="address"
              value={form.address}
              onChange={onChange}
              rows={3}
              className="mt-1 w-full bg-[#fafaf9] border border-stone-300 rounded-sm px-3 py-2.5 focus:outline-none focus:border-[#274e13]"
              required
            />
          </div>
          <div>
            <label className="text-xs tracking-wider uppercase text-stone-500 font-semibold">{t.checkout.notes}</label>
            <input
              data-testid="checkout-notes"
              name="notes"
              value={form.notes}
              onChange={onChange}
              className="mt-1 w-full bg-[#fafaf9] border border-stone-300 rounded-sm px-3 py-2.5 focus:outline-none focus:border-[#274e13]"
            />
          </div>

          <div>
            <div className="text-xs tracking-wider uppercase text-stone-500 font-semibold mb-2">
              {t.checkout.paymentMethod}
            </div>
            <div
              data-testid="payment-whatsapp"
              className="flex items-start gap-3 p-4 border border-[#274e13] bg-[#f2ebd9] rounded-sm"
            >
              <MessageCircle size={20} className="text-[#274e13] mt-0.5 shrink-0" />
              <div className="flex-1">
                <div className="font-semibold text-stone-900">{t.checkout.whatsapp}</div>
                <div className="text-xs text-stone-600 mt-1">
                  {lang === "en"
                    ? "Pay by Cash or UPI when your order is delivered."
                    : "ఆర్డర్ డెలివరీ సమయంలో నగదు లేదా UPI ద్వారా చెల్లించండి."}
                </div>
              </div>
            </div>
          </div>

          <button
            type="submit"
            data-testid="place-order-btn"
            disabled={submitting}
            className="w-full bg-[#274e13] hover:bg-[#38761d] text-white py-3.5 rounded-sm font-semibold tracking-wide transition-colors disabled:opacity-60"
          >
            {submitting ? t.checkout.sending : t.checkout.placeOrder}
          </button>
        </form>

        <div className="lg:col-span-2">
          <div className="bg-[#f2ebd9] border border-stone-200 rounded-sm p-6 sticky top-24">
            <div className="text-[11px] tracking-[0.2em] uppercase text-stone-500 font-semibold mb-4">
              {lang === "en" ? "Order summary" : "ఆర్డర్ సారాంశం"}
            </div>
            <div className="space-y-3 max-h-72 overflow-auto pr-1">
              {cart.map((it) => (
                <div key={`${it.product_id}_${it.weight_kg}`} className="flex justify-between text-sm">
                  <div className="flex-1 pr-2">
                    <div className="font-medium text-stone-900">
                      {lang === "en" ? it.name_en : it.name_te}
                    </div>
                    <div className="text-xs text-stone-500">
                      {it.weight_kg}kg × {it.quantity}
                    </div>
                  </div>
                  <div className="font-semibold">
                    ₹{(it.weight_kg * it.price_per_kg * it.quantity).toFixed(0)}
                  </div>
                </div>
              ))}
            </div>
            <div className="border-t border-stone-300 mt-5 pt-4 flex justify-between items-center">
              <span className="text-stone-700">{t.common.total}</span>
              <span className="text-2xl font-bold text-[#274e13]" data-testid="checkout-total">
                ₹{Math.max(0, cartTotal - (couponInfo?.discount_amount || 0)).toFixed(0)}
              </span>
            </div>
            {couponInfo && (
              <div className="text-xs text-green-700 font-semibold mt-1 text-right">
                Coupon {couponCode.toUpperCase()} applied — saved ₹{couponInfo.discount_amount.toFixed(0)}
              </div>
            )}
            <div className="mt-5 pt-5 border-t border-stone-300">
              <div className="text-[11px] tracking-wider uppercase text-stone-500 font-semibold mb-2">
                Got a coupon code?
              </div>
              <div className="flex gap-2">
                <input
                  data-testid="coupon-input"
                  value={couponCode}
                  onChange={(e) => { setCouponCode(e.target.value); setCouponError(""); setCouponInfo(null); }}
                  placeholder="WELCOME10"
                  className="flex-1 bg-white border border-stone-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#274e13] uppercase"
                />
                <button
                  type="button"
                  data-testid="coupon-apply"
                  onClick={async () => {
                    if (!couponCode.trim()) return;
                    setCouponError("");
                    try {
                      const { data } = await api.post("/coupons/validate", null, {
                        params: { code: couponCode.trim().toUpperCase(), subtotal: cartTotal },
                      });
                      setCouponInfo(data);
                    } catch (err) {
                      setCouponInfo(null);
                      setCouponError(err.response?.data?.detail || "Invalid coupon");
                    }
                  }}
                  className="px-4 py-2 bg-[#274e13] hover:bg-[#38761d] text-white rounded-sm text-sm font-semibold"
                >
                  Apply
                </button>
              </div>
              {couponError && <div className="text-xs text-red-600 mt-1" data-testid="coupon-error">{couponError}</div>}
            </div>
          </div>
        </div>
      </div>

      {payModal && (
        <div
          data-testid="upi-pay-modal"
          className="fixed inset-0 bg-black/50 grid place-items-center z-50 p-4"
        >
          <div className="bg-white w-full max-w-md rounded-sm overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-stone-200">
              <div>
                <div className="text-[11px] tracking-[0.2em] uppercase text-[#b45f06] font-semibold">
                  {lang === "en" ? "Order placed" : "ఆర్డర్ ఇవ్వబడింది"}
                </div>
                <div className="text-lg font-semibold text-stone-900">
                  #{payModal.trackingCode}
                </div>
              </div>
              <button
                data-testid="upi-modal-close"
                onClick={closePayModal}
                className="p-1 text-stone-500 hover:text-stone-900"
              >
                <X size={18} />
              </button>
            </div>

            <div className="px-6 py-6 text-center">
              <div className="text-sm text-stone-700">
                {lang === "en"
                  ? "Pay now via UPI (optional) or pay on delivery"
                  : "ఇప్పుడే UPI ద్వారా చెల్లించండి (ఐచ్ఛికం) లేదా డెలివరీ సమయంలో చెల్లించండి"}
              </div>
              <div className="text-3xl font-bold text-[#274e13] mt-2">
                ₹{payModal.total.toFixed(0)}
              </div>

              {business?.upi_id ? (
                <>
                  <a
                    href={upiLink}
                    data-testid="upi-qr-link"
                    className="mt-5 inline-block p-3 bg-white border border-stone-300 rounded-sm hover:border-[#274e13] transition-colors"
                    aria-label="Open in UPI app"
                  >
                    <div data-testid="upi-qr-wrapper">
                      <QRCodeSVG value={upiLink} size={196} level="M" includeMargin={false} />
                    </div>
                  </a>
                  <div className="mt-3 text-xs tracking-wider uppercase text-stone-500 font-semibold">
                    {lang === "en"
                      ? "Tap QR to pay · or scan from another phone"
                      : "చెల్లించడానికి QR నొక్కండి · లేదా మరో ఫోన్ నుండి స్కాన్ చేయండి"}
                  </div>
                  <a
                    href={upiLink}
                    data-testid="upi-pay-btn"
                    className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-[#5f259f] hover:bg-[#4a1c7d] text-white py-3 rounded-sm font-semibold tracking-wide transition-colors"
                  >
                    {lang === "en" ? "Pay ₹" : "₹"}
                    {payModal.total.toFixed(0)}
                    {lang === "en" ? " via UPI app" : " చెల్లించండి (UPI)"}
                  </a>
                  <button
                    data-testid="copy-upi-id"
                    onClick={copyUpiId}
                    className="mt-2 inline-flex items-center gap-2 text-xs bg-stone-100 hover:bg-stone-200 px-3 py-2 rounded-sm border border-stone-300"
                  >
                    {copied ? <Check size={14} className="text-green-700" /> : <Copy size={14} />}
                    <span className="font-mono">{business.upi_id}</span>
                  </button>
                </>
              ) : null}

              <button
                data-testid="send-whatsapp-btn"
                onClick={sendOnWhatsApp}
                className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-[#274e13] hover:bg-[#38761d] text-white py-3 rounded-sm font-semibold tracking-wide transition-colors"
              >
                <MessageCircle size={16} />
                {lang === "en" ? "Send order on WhatsApp" : "వాట్సాప్ లో ఆర్డర్ పంపండి"}
              </button>
              <a
                data-testid="track-order-link"
                href={`/track/${payModal.trackingCode}`}
                className="mt-2 w-full inline-flex items-center justify-center gap-2 bg-white border border-stone-300 hover:bg-stone-50 text-stone-900 py-3 rounded-sm font-semibold tracking-wide transition-colors"
              >
                {lang === "en" ? "Track your order" : "మీ ఆర్డర్ ట్రాక్ చేయండి"}
              </a>
              <button
                data-testid="upi-modal-done"
                onClick={closePayModal}
                className="mt-2 w-full text-sm text-stone-500 hover:text-stone-900 py-2"
              >
                {lang === "en" ? "Done" : "పూర్తయింది"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
